/**
 * Ranks and percentiles for the Explorer grid's chips: which way is good, and against whom.
 *
 * - **Direction** has two layers. A column's *base* direction is the offense's view: the glossary's `direction` per
 *   metric (`lib/metric.py`: higher, lower, or neutral = a frequency or volume, ranked by size, never good or bad),
 *   unless the reader set it (`rd` in the table state, stored in the offense's terms). The query's *perspective* then
 *   applies on top: on a defense's view higher / lower flip, so a low PPP allowed is green. The perspective is
 *   detected (exec_query `layout.perspective`) unless the reader picked one (`pv`). Flipping it flips every
 *   non-neutral column, overridden ones too: an override says "for this metric, lower is the offense's good", not
 *   "paint low green whatever the view".
 * - **Population**: the rows that pass the column filters, pinned rows always included (the quick filter is a search,
 *   not a population). "Rank across" a breakdown (`ra`) ranks a row only against rows sharing every *other*
 *   breakdown's value: across TeamOffense, POR's PR row ranks against the other teams' PR rows (the team report's
 *   rule); across ActionSimple, POR's actions rank against each other. "All rows" (the default) is one population.
 */

import type { Perspective, ResultLayout } from "../../../models/Explorer";

export type RankDirection = "higher" | "lower" | "neutral";
/** The table state's compact form of a direction override. */
export type DirectionCode = "h" | "l" | "n";

export const DIRECTION_CODES: Record<RankDirection, DirectionCode> = {
  higher: "h",
  lower: "l",
  neutral: "n",
};
export const CODE_DIRECTIONS: Record<DirectionCode, RankDirection> = {
  h: "higher",
  l: "lower",
  n: "neutral",
};

export type { Perspective, ResultLayout };

export interface ColumnRank {
  /** 0-100, 100 = best (largest for a neutral column). */
  percentile: number;
  /** 1 = best; ties share the better rank. */
  rank: number;
  /** How many rows it was ranked against. */
  population: number;
}

/** Registry metric names a result column may stand for, most specific first. A named metric group prefixes its
 *  columns (`HC_PPP`, and a group name may hold underscores), a group dimension pivots them (`PPP-PR`), and the
 *  grid's Overall denormalisation names its hidden columns `"<value>: <column>"`. */
export function metricNameCandidates(field: string): string[] {
  const colon = field.indexOf(": ");
  const base = colon >= 0 ? field.slice(colon + 2) : field;
  const candidates = [base];
  for (let u = base.indexOf("_"); u > 0; u = base.indexOf("_", u + 1)) {
    candidates.push(base.slice(u + 1));
  }
  candidates.slice().forEach((c) => {
    const dash = c.indexOf("-");
    if (dash > 0) candidates.push(c.slice(0, dash));
  });
  return candidates;
}

const FLIP: Record<RankDirection, RankDirection> = {
  higher: "lower",
  lower: "higher",
  neutral: "neutral",
};

/**
 * A column's base direction (the offense's view) before any override.
 *
 * @param lookup a registry metric's direction, undefined when the name isn't a metric
 * @param isCustom a custom column: whatever it computes is its author's, so it keeps the old default (higher)
 */
export function baseDirection(
  field: string,
  {
    lookup,
    isCustom = false,
  }: {
    lookup: (name: string) => RankDirection | undefined;
    isCustom?: boolean;
  },
): RankDirection {
  // A share of the primary group (dimensionRatio): how much of the work, not how well
  const colon = field.indexOf(": ");
  const base = colon >= 0 ? field.slice(colon + 2) : field;
  if (base.startsWith("% of ")) return "neutral";
  if (isCustom) return "higher";
  for (const name of metricNameCandidates(field)) {
    const found = lookup(name);
    if (found) return found;
  }
  return "higher";
}

/** A base direction seen from `view`: a defense's view flips higher / lower; neutral never moves. The same function
 *  turns an effective direction back into a base one (flipping twice is the identity). */
export function inView(direction: RankDirection, view: "off" | "def"): RankDirection {
  return view === "def" ? FLIP[direction] : direction;
}

/** The view the chips use: the reader's pick, else the detected one ("mixed" and none read as the offense's). */
export function resolveView(
  picked: "off" | "def" | null | undefined,
  detected: Perspective | undefined,
): "off" | "def" {
  return picked ?? (detected === "def" ? "def" : "off");
}

/** The direction the grid uses: the column's base (override, else default) seen from the view. */
export function effectiveDirection(
  field: string,
  overrides: Record<string, DirectionCode>,
  fallback: () => RankDirection,
  view: "off" | "def" = "off",
): RankDirection {
  const code = overrides[field];
  return inView(code ? CODE_DIRECTIONS[code] : fallback(), view);
}

export function directionLabel(direction: RankDirection): string {
  return direction === "higher"
    ? "Higher is better"
    : direction === "lower"
      ? "Lower is better"
      : "Neutral (ranked by size)";
}

/**
 * The breakdown columns a result has, in order: exec_query's layout when there is one, else the grid's own guess
 * (columns named like a filter).
 */
export function breakdownColumns(
  layout: ResultLayout | undefined | null,
  dataColumns: string[],
  fallback: string[],
): string[] {
  if (!layout) return fallback;
  const present = new Set(dataColumns);
  return layout.dimensions.map((d) => d.column).filter((c) => present.has(c));
}

/** The partition for "rank across `across`": every other breakdown column. None (all rows) when `across` is null
 *  or names no breakdown of this result. Several columns rank across their combinations: a report's game link ranks
 *  across team and game, so each team-game ranks against every other within its action. */
export function partitionColumns(
  across: string | string[] | null | undefined,
  breakdowns: string[],
): string[] {
  const picked = (Array.isArray(across) ? across : across ? [across] : []).filter((c) => breakdowns.includes(c));
  if (picked.length === 0) return [];
  return breakdowns.filter((c) => !picked.includes(c));
}

const numeric = (raw: unknown): number => {
  if (raw === null || raw === undefined || raw === "" || raw === "inf") {
    return NaN;
  }
  return parseFloat(String(raw));
};

/**
 * Rank one column. Rows with no number get nothing; a population of one gets nothing either (a lone row has no one
 * to be better or worse than, and a 0th percentile would paint it red).
 *
 * Percentile keeps the grid's long-standing formula over the rows in good-to-best order (worst first): the worst is
 * 0, the best 100, the rest (position + 1) / n. Ties take their first position.
 */
export function rankColumn<Row extends Record<string, any>>(
  rows: Row[],
  field: string,
  direction: RankDirection,
  partition: string[],
  idOf: (row: Row) => string | number,
): Map<string | number, ColumnRank> {
  const groups = new Map<string, { id: string | number; value: number }[]>();
  for (const row of rows) {
    const value = numeric(row[field]);
    if (Number.isNaN(value)) continue;
    const key = partition.map((c) => String(row[c] ?? "")).join("\u0000");
    let group = groups.get(key);
    if (!group) groups.set(key, (group = []));
    group.push({ id: idOf(row), value });
  }

  const out = new Map<string | number, ColumnRank>();
  const lowerIsBetter = direction === "lower";
  groups.forEach((group) => {
    const n = group.length;
    if (n < 2) return;
    // Worst first
    const ascending = group.map((g) => g.value).sort((a, b) => a - b);
    const worstFirst = lowerIsBetter ? ascending.slice().reverse() : ascending;
    const firstAt = new Map<number, number>();
    worstFirst.forEach((v, i) => {
      if (!firstAt.has(v)) firstAt.set(v, i);
    });
    // Best first, for ranks (ties share the better rank)
    const bestFirst = worstFirst.slice().reverse();
    const rankAt = new Map<number, number>();
    bestFirst.forEach((v, i) => {
      if (!rankAt.has(v)) rankAt.set(v, i + 1);
    });
    for (const { id, value } of group) {
      const i = firstAt.get(value)!;
      const percentile =
        i === 0 ? 0 : i === n - 1 ? 100 : Math.round(((i + 1) / n) * 100);
      out.set(id, { percentile, rank: rankAt.get(value)!, population: n });
    }
  });
  return out;
}

/**
 * A lazy, per-column cache over one population: the grid builds this once per change of rows, filters, directions
 * or layout, and each column is ranked the first time one of its cells renders (most columns, like the hidden
 * Overall denormalisations, never do).
 */
export function createRankLookup<Row extends Record<string, any>>(
  population: Row[],
  directionOf: (field: string) => RankDirection,
  partition: string[],
  idOf: (row: Row) => string | number,
) {
  const cache = new Map<string, Map<string | number, ColumnRank>>();
  return (field: string, id: string | number): ColumnRank | undefined => {
    let column = cache.get(field);
    if (!column) {
      column = rankColumn(population, field, directionOf(field), partition, idOf);
      cache.set(field, column);
    }
    return column.get(id);
  };
}
